import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatUtcDate } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  DEFAULT_EXPIRATION_PRESET,
  DEFAULT_SCOPE_SELECTION,
  FULL_SCOPE_SELECTION,
  endOfPickedDayUtc,
  startOfTomorrowLocal,
  EXPIRATION_PRESET_LABELS,
  EXPIRATION_PRESET_ORDER,
  MANAGEMENT_KEY_NAME_MAX_LENGTH,
  resolveExpiration,
  scopeSelectionToList,
  type ExpirationPreset,
  type ScopeLevel,
  type ScopeSelection,
} from '@/lib/management-key-utils';
import {
  V1_SCOPE_RESOURCES,
  V1_SCOPE_RESOURCE_DESCRIPTIONS,
  V1_SCOPE_RESOURCE_LABELS,
  writeScopeFor,
  type V1Scope,
} from '@kirin/api-client/contracts/v1/scopes';

/** The three ways to pick scopes, in the order the select renders them. */
type AccessPreset = 'read_only' | 'read_write' | 'custom';

const ACCESS_PRESET_LABELS: Readonly<Record<AccessPreset, string>> = {
  read_only: 'Read-only — every resource',
  read_write: 'Read & write — every resource',
  custom: 'Custom — choose per resource',
};

const ACCESS_PRESET_ORDER: readonly AccessPreset[] = [
  'read_only',
  'read_write',
  'custom',
];

/** Per-resource level labels. `write` says "Read & write" because it confers both. */
const SCOPE_LEVEL_LABELS: Readonly<Record<ScopeLevel, string>> = {
  none: 'No access',
  read: 'Read',
  write: 'Read & write',
};

interface CreateManagementApiKeyDialogProps {
  /**
   * Dismiss the dialog. The parent unmounts on this — see the component doc.
   */
  onClose: () => void;
  /**
   * Submits the key. Rejecting is expected — the caller surfaces the error and
   * this dialog stays open so the user does not lose what they typed.
   */
  onCreate: (input: {
    name: string;
    expiresAt?: Date;
    scopes: V1Scope[];
  }) => Promise<void>;
  isPending: boolean;
}

/**
 * Create-key form for management API keys (KIR-3748).
 *
 * **Mounted only while open.** The parent renders it conditionally rather than
 * passing an `isOpen` prop, so every dismissal path — Cancel, Esc, backdrop,
 * and the programmatic close after a successful create — unmounts the form and
 * resets it. Radix fires `onOpenChange` only for user-driven dismissals, so a
 * handler-based reset would leave the previous key's name in the field the next
 * time the dialog opened.
 *
 * Client-side validation here is load-bearing, not cosmetic: the contract's
 * `expiresAt` carries a `.refine(future)` that throws a `SchemaParseError`
 * *before the request is sent*, so an empty name or a past date would surface
 * as an opaque client crash rather than a field error. The calendar therefore
 * disables everything before tomorrow, and Create stays disabled until the name
 * is non-empty and a custom date (if chosen) has been picked.
 */
export function CreateManagementApiKeyDialog({
  onClose,
  onCreate,
  isPending,
}: CreateManagementApiKeyDialogProps): ReactElement {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<ExpirationPreset>(
    DEFAULT_EXPIRATION_PRESET
  );
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const timeZone = useDisplayTimeZone();
  const [accessPreset, setAccessPreset] = useState<AccessPreset>('read_only');
  // Only consulted while `accessPreset === 'custom'`. Seeded from the read-only
  // default so switching to Custom starts from what the user already had rather
  // than from an empty grid they must fill in from scratch.
  const [customScopes, setCustomScopes] = useState<ScopeSelection>(
    DEFAULT_SCOPE_SELECTION
  );

  const selection: ScopeSelection =
    accessPreset === 'read_only'
      ? DEFAULT_SCOPE_SELECTION
      : accessPreset === 'read_write'
        ? FULL_SCOPE_SELECTION
        : customScopes;

  const scopes = useMemo(
    () => scopeSelectionToList(selection),
    // `selection` is a fresh object each render only in the preset cases, where
    // it is one of two frozen module constants; in the custom case it is the
    // state object itself. Either way its identity tracks the actual choice.
    [selection]
  );

  // Memoized so react-day-picker, which compares the matcher by reference,
  // does not re-evaluate every disabled day on each render.
  const minDate = useMemo(() => startOfTomorrowLocal(), []);

  const trimmedName = name.trim();
  const canSubmit =
    trimmedName.length > 0 &&
    trimmedName.length <= MANAGEMENT_KEY_NAME_MAX_LENGTH &&
    (preset !== 'custom' || customDate !== undefined) &&
    // Same reason the name is checked here: the contract's `.min(1)` throws a
    // `SchemaParseError` before the request is sent, so an all-"No access"
    // selection would surface as an opaque client crash rather than a disabled
    // button. A key with no scopes could reach nothing anyway.
    scopes.length > 0 &&
    !isPending;

  // Blocks Esc and backdrop clicks while the create is in flight, matching the
  // disabled Cancel button — dismissing mid-request would tear down the only
  // component that can catch and surface the result.
  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open && !isPending) onClose();
    },
    [isPending, onClose]
  );

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    const expiresAt =
      preset === 'custom'
        ? // End of the chosen calendar day in UTC. Built from the day's *local*
          // parts — see `endOfPickedDayUtc` for why the UTC parts are wrong.
          customDate && endOfPickedDayUtc(customDate)
        : resolveExpiration(preset);

    await onCreate({ name: trimmedName, expiresAt, scopes });
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      {/*
        Wider than the `sm:max-w-lg` default (KIR-4377 review, KIR-4880): the
        Custom matrix puts a resource label, its description and a level select
        on one row. Fixed rather than widening only for Custom, so the dialog
        never resizes under the cursor mid-choice.
      */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create management API key</DialogTitle>
          <DialogDescription>
            The key is shown once, immediately after it is created. Store it
            somewhere safe — it cannot be retrieved again.
          </DialogDescription>
        </DialogHeader>

        {/*
          `min-w-0` because `DialogContent` is a grid, and a grid item's default
          `min-width: auto` let the Custom rows' one-line descriptions stretch
          this body past the dialog edge instead of shrinking (KIR-4880).
        */}
        <div className="min-w-0 space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="management-key-name">Name</Label>
            <Input
              id="management-key-name"
              value={name}
              maxLength={MANAGEMENT_KEY_NAME_MAX_LENGTH}
              placeholder="e.g. Splunk connector"
              onChange={event => setName(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="management-key-access">Access scopes</Label>
            <Select
              value={accessPreset}
              onValueChange={value => {
                const next = value as AccessPreset;
                // Carry the levels the user can currently see into the matrix,
                // so Custom opens on what they already had rather than silently
                // resetting. Without this, picking "Read & write" and then
                // Custom to narrow one resource dropped every write — the exact
                // opposite of the narrowing they asked for, and invisible until
                // the key failed in production.
                if (next === 'custom') {
                  setCustomScopes(selection);
                }
                setAccessPreset(next);
              }}
            >
              <SelectTrigger id="management-key-access">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_PRESET_ORDER.map(value => (
                  <SelectItem key={value} value={value}>
                    {ACCESS_PRESET_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              What this key may reach. It can never exceed your own permissions,
              and cannot be changed after the key is created.
            </p>
          </div>

          {accessPreset === 'custom' && (
            <div className="space-y-2 rounded-md border p-3">
              {V1_SCOPE_RESOURCES.map(resource => {
                const hasWrite = writeScopeFor(resource) !== null;
                return (
                  <div
                    key={resource}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <Label
                        htmlFor={`management-key-scope-${resource}`}
                        className="text-sm"
                      >
                        {V1_SCOPE_RESOURCE_LABELS[resource]}
                      </Label>
                      {/* Wraps rather than truncates: a clipped description
                          hides what the scope actually grants. */}
                      <p className="text-muted-foreground text-xs">
                        {V1_SCOPE_RESOURCE_DESCRIPTIONS[resource]}
                      </p>
                    </div>
                    <Select
                      value={customScopes[resource]}
                      onValueChange={value =>
                        setCustomScopes(current => ({
                          ...current,
                          [resource]: value as ScopeLevel,
                        }))
                      }
                    >
                      <SelectTrigger
                        id={`management-key-scope-${resource}`}
                        className="w-40 shrink-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {SCOPE_LEVEL_LABELS.none}
                        </SelectItem>
                        <SelectItem value="read">
                          {SCOPE_LEVEL_LABELS.read}
                        </SelectItem>
                        {/* Omitted, not disabled, where the resource has no
                            writable endpoints: an option that exists but can
                            never be chosen reads as a bug. */}
                        {hasWrite && (
                          <SelectItem value="write">
                            {SCOPE_LEVEL_LABELS.write}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="management-key-expiration">Expiration</Label>
            <Select
              value={preset}
              onValueChange={value => {
                setPreset(value as ExpirationPreset);
                if (value !== 'custom') setCustomDate(undefined);
              }}
            >
              <SelectTrigger id="management-key-expiration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRATION_PRESET_ORDER.map(value => (
                  <SelectItem key={value} value={value}>
                    {EXPIRATION_PRESET_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {preset === 'custom' && (
            <div className="space-y-1.5">
              <Label>Expires on</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {/* Formatted from the normalised instant, not the raw
                        picked day: `formatUtcDate` reads UTC parts, so labelling
                        `customDate` directly showed the previous day east of
                        UTC — the same off-by-one as the submitted value. */}
                    {customDate
                      ? formatUtcDate(
                          endOfPickedDayUtc(customDate),
                          timeZone !== 'UTC'
                        )
                      : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customDate}
                    onSelect={date => {
                      setCustomDate(date);
                      if (date) setCalendarOpen(false);
                    }}
                    disabled={{ before: minDate }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isPending ? 'Creating…' : 'Create key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
